import React, { useState } from 'react'
import { ProjectProps } from '../Project'
import { FunctionModel } from '../../../utils/interfaces'
import { useTranslation } from 'react-i18next'
import { Admin } from '../../../api/API'
import { BoldHeader3 } from '../../../components/General'
import { Input, Textarea } from '../../../components/Input'
import { isValidTypeString, useEventListener } from '../../../utils/functions'
import DropDown from '../../../components/DropDown'
import { BiTrash } from 'react-icons/bi'
import Loading from '../../../components/Loading'

interface Props extends ProjectProps {
  func: FunctionModel
  httpMethods: string[]
  httpMethod: string
  setHttpMethod: React.Dispatch<React.SetStateAction<string>>
  queryParams: string
  setQueryParams: React.Dispatch<React.SetStateAction<string>>
  headers: { name: string, value: string }[]
  setHeaders: React.Dispatch<React.SetStateAction<{ name: string, value: string }[]>>
  body: string
  setBody: React.Dispatch<React.SetStateAction<string>>
  response: { statusCode: number, body: string } | null
  setResponse: React.Dispatch<React.SetStateAction<{ statusCode: number, body: string } | null>>
  isResponding: boolean
  setIsResponding: React.Dispatch<React.SetStateAction<boolean>>
  showHttpMethodDropdown: boolean
  setShowHttpMethodDropdown: React.Dispatch<React.SetStateAction<boolean>>
}

const FunctionTest = (props: Props) => {

  const { t } = useTranslation();
  const [showTestTemplates, setShowTestTemplates] = useState(false);

  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('#http-method-dropdown') && !target.closest('.http-method-dropdown-button')) {
      props.setShowHttpMethodDropdown(false);
    }
  }, []);


  // editor event handlers
  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // prevent tab escape
    if (e.key === 'Tab') {
      e.preventDefault();
      const inputElement = (document.getElementById('json-editor-input') as HTMLTextAreaElement);
      const cursorPosition = inputElement.selectionStart;
      if (cursorPosition === null) return;
      const value = inputElement.value;
      const newValue = value.slice(0, cursorPosition) + '\t' + value.slice(cursorPosition);
      props.setBody(newValue);
      setTimeout(() => {
        inputElement?.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
      }, 0);
    }
  }
  const handleEditorTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const currentValue = e.target.value;
    if (currentValue.length < props.body.length) { // is deleting text
      props.setBody(currentValue);
      return;
    }
    const cursorPosition = e.target.selectionStart;
    const newChar = currentValue[cursorPosition - 1];
    if (newChar === '{' || newChar === '[' || newChar === '"') { // is adding {, [, "
      const newValue = currentValue.slice(0, cursorPosition) + (newChar === '{' ? '}' : newChar === '[' ? ']' : '"') + currentValue.slice(cursorPosition);
      props.setBody(newValue);
      setTimeout(() => {
        (document.getElementById('json-editor-input') as HTMLInputElement)?.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
    } else {
      props.setBody(currentValue);
    }
  }

  const submit = async () => {
    props.setResponse(null);
    props.setIsResponding(true);
    const res = await Admin.testFunction(props.project.id, props.func.id, props.httpMethod, props.queryParams, props.headers, props.body);
    props.setResponse({ statusCode: res.statusCode, body: typeof res.body === 'string' ? res.body : JSON.stringify(res.body) });
    props.setIsResponding(false);
  }

  return (
    <>
      <div>
        <div className='flex items-center justify-between'>
          <BoldHeader3>{t('project.function.test.title')}</BoldHeader3>
          <div onMouseEnter={() => setShowTestTemplates(true)} onMouseLeave={() => setShowTestTemplates(false)} className='text-xs underline cursor-pointer relative'>{t('project.function.test.template')}
            {showTestTemplates && <div className='absolute top-full right-0 bg-black text-white rounded-md p-2 z-10 w-40' onMouseEnter={() => setShowTestTemplates(true)} onMouseLeave={() => setShowTestTemplates(false)}>
              <div>{t('project.function.test.template-message')}</div>
              <div className='flex gap-1 flex-col items-start mt-2'>
                <button onClick={() => {props.setHttpMethod('POST'); props.setHeaders([{ name: 'Content-Type', value: 'application/json' }]); props.setBody('{\n\t"username": "your_username",\n\t"password": "your_password"\n}'); props.setQueryParams('')}} className='underline'>Sign in</button>
                <button onClick={() => {props.setHttpMethod('GET'); props.setHeaders([]); props.setBody(''); props.setQueryParams('tableName=your_table&id=your_item_id')}} className='underline'>Get item</button>
              </div>
            </div>}
          </div>
        </div>
        <div className='text-xs font-light text-gray-400 overflow-hidden text-ellipsis'>{`https://api.tranfoo.com/${props.project.id}/function/${props.func.id}`}</div>
        <div className='mt-4'>
          <label className='text-xs text-gray-400 m-1'>{t('project.function.test.http-method')}</label>
          <div className='relative'>
            <Input extraclass='http-method-dropdown-button cursor-pointer' onClick={() => props.setShowHttpMethodDropdown(true)} textSize='small' value={props.httpMethod} readOnly />
            {props.showHttpMethodDropdown && <DropDown
              id='http-method-dropdown'
              buttonStyle={{ textAlign: 'left' }}
              options={props.httpMethods}
              onSelect={option => { props.setHttpMethod(option); props.setShowHttpMethodDropdown(false) }}
            />}
          </div>
        </div>
        <div className='mt-4'>
          <label className='text-xs text-gray-400 m-1'>{t('project.function.test.query-params')}</label>
          <Input placeholder='param1=value1&param2=value2' extraclass='http-method-dropdown-button' textSize='small' value={props.queryParams} onChange={e => props.setQueryParams(e.target.value)} />
        </div>
        <div className='mt-4 flex flex-col'>
          <label className='text-xs text-gray-400 m-1'>{t('project.function.test.headers')}</label>
          <div className='flex flex-col gap-2'>
            {props.headers.map((header, index) => (
              <div className='flex items-center'>
                <Input textSize='small' placeholder={`Header ${index + 1}`} style={{ maxWidth: '160px' }} value={header.name} onChange={e => props.setHeaders(props.headers.map((h, i) => i === index ? { name: e.target.value, value: header.value } : h))} />
                <span className='mx-2'>:</span>
                <Input textSize='small' placeholder={`Value ${index + 1}`} style={{ maxWidth: '160px' }} value={header.value} onChange={e => props.setHeaders(props.headers.map((h, i) => i === index ? { name: header.name, value: e.target.value } : h))} />
                <button onClick={() => props.setHeaders(props.headers.filter((h, i) => i !== index))} className='flex items-center justify-center w-6 h-8 text-gray-400 ml-2'><BiTrash /></button>
              </div>
            ))}
          </div>
          <div className='mt-2'><button onClick={() => props.setHeaders([...props.headers, { name: '', value: '' }])} className='text-xs bg-gray-100 py-1.5 px-6 rounded-lg'>{t('project.function.test.add-header')}</button></div>
        </div>
        {props.httpMethod !== 'GET' && <div className='mt-4'>
          <label className='text-xs text-gray-400 m-1'>{t('project.function.test.body')}</label>
          <Textarea id='json-editor-input' extraclass={`${props.body === '' || isValidTypeString('map', props.body) ? '' : 'border-red-400 focus:border-red-400'} font-mono overflow-auto`} rows={8} wrap='off'
            textSize='small' value={props.body} onChange={handleEditorTextChange} onKeyDown={handleEditorKeyDown}
            placeholder='{ "key": "value" }'
          />
        </div>}
        <div className='mt-4'>
          <button onClick={submit} disabled={props.isResponding || (!!props.body && !isValidTypeString('map', props.body))} className='text-xs text-white bg-primary disabled:bg-gray-300 flex items-center justify-center h-8 px-8 rounded-lg'>{props.isResponding ? <Loading /> : t('general.test')}</button>
        </div>

        <div className='my-4 border-t' />

        {props.response && <>
          <BoldHeader3>{t('project.function.test.response')}</BoldHeader3>
          <div className='mt-8'>
            <div className='text-sm'>{t('project.function.test.status-code')}</div>
            <div className='mt-2'>{props.response?.statusCode}</div>
          </div>
          <div className='mt-8'>
            <div className='text-sm'>{t('project.function.test.response.body')}</div>
            <div className='mt-2 font-mono text-xs md:text-sm break-all'>{props.response?.body}</div>
          </div>
        </>}
      </div>
    </>
  )
}

export default FunctionTest